import ArchiveIcon from '@mui/icons-material/Archive'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import SportsScoreIcon from '@mui/icons-material/SportsScore'
import { Button } from '@mui/material'
import { useState } from 'react'
import {
  BulkActionsToolbarProps,
  RaRecord,
  useGetList,
  useRecordContext,
  useTranslate,
  useUpdate,
  useNotify,
  useRefresh,
  useRedirect,
} from 'react-admin'
import { ActionsToolbar } from '../../components'
import { xxllncColor } from '../../layout/themes'
import { getUrl, httpClient } from '../../providers/data'
import { Samenwerking } from '../../types/apiRequests'
import { useOrganizationSettings } from '../../utils/useOrganizationSettings'

interface CollaborationsShowActionsProps extends BulkActionsToolbarProps {
  data?: RaRecord
}

export const CollaborationsShowActions = (
  props: CollaborationsShowActionsProps
): JSX.Element => {
  const translate = useTranslate()
  const record = useRecordContext<Samenwerking>(props)
  const id = record?.id
  const status = record?.status

  const [update, { isLoading }] = useUpdate()
  const [isArchiving, setIsArchiving] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()

  const orgSettings = useOrganizationSettings()
  const isOwner = !!(orgSettings?.oin_number && record?.aangemaaktDoor === orgSettings.oin_number)
  const isClosed = status === 'GESLOTEN'

  const { total: auditLogCount } = useGetList(
    `samenwerkingen/${id}/auditlog`,
    { pagination: { page: 1, perPage: 1 }, sort: { field: 'created', order: 'DESC' } },
    { enabled: !!id && isClosed }
  )
  const hasAuditLogs = (auditLogCount ?? 0) > 0

  const onClose = () => {
    if (!id) return
    void update(
      'samenwerkingen',
      { id, data: { status: 'GESLOTEN' }, previousData: record },
      {
        onSuccess: () => {
          notify('Samenwerking succesvol gesloten', { type: 'success' })
          refresh()
        },
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : 'Onbekende fout'
          notify(`Fout bij sluiten samenwerking: ${message}`, { type: 'error' })
        },
      }
    )
  }

  const onReopen = () => {
    if (!id) return
    void update(
      'samenwerkingen',
      { id, data: { status: 'OPEN' }, previousData: record },
      {
        onSuccess: () => {
          notify('Samenwerking succesvol heropend', { type: 'success' })
          refresh()
        },
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : 'Onbekende fout'
          notify(`Fout bij heropenen samenwerking: ${message}`, { type: 'error' })
        },
      }
    )
  }

  const onArchive = () => {
    if (!id) return
    if (!window.confirm(translate('data.confirmArchiveSamenwerking'))) return
    setIsArchiving(true)
    httpClient(`${getUrl()}/samenwerkingen/${id}/archive`, { method: 'POST' })
      .then(() => {
        notify('Samenwerking succesvol gearchiveerd', { type: 'success' })
        redirect('/samenwerkingen')
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Onbekende fout'
        notify(`Fout bij archiveren samenwerking: ${message}`, { type: 'error' })
      })
      .finally(() => setIsArchiving(false))
  }

  const buttonSx = {
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: xxllncColor.dark100,
    borderRadius: '10px',
  } as const

  return (
    <ActionsToolbar source="titel">
      <Button
        variant="outlined"
        endIcon={<SportsScoreIcon />}
        onClick={onClose}
        size="small"
        disabled={isClosed || isLoading}
        sx={buttonSx}
      >
        {isClosed ? translate('data.closed') : translate('data.complete')}
      </Button>
      {isClosed && isOwner && (
        <Button
          variant="outlined"
          endIcon={<LockOpenIcon />}
          onClick={onReopen}
          size="small"
          disabled={isLoading}
          sx={buttonSx}
        >
          {translate('data.reopenSamenwerking')}
        </Button>
      )}
      {isClosed && (
        <Button
          variant="outlined"
          endIcon={<ArchiveIcon />}
          onClick={onArchive}
          size="small"
          disabled={isArchiving || !hasAuditLogs}
          sx={buttonSx}
        >
          {translate('data.archiveSamenwerking')}
        </Button>
      )}
    </ActionsToolbar>
  )
}
